import React, { useEffect, useRef, useState } from "react";
import type { ManifestAsset, ManifestTaxonomy } from "../../types.js";
import { getIdToken } from "../../admin/auth.js";

interface Props {
  asset: ManifestAsset;
  taxonomy: ManifestTaxonomy;
  albumPaths: string[];
  onClose: () => void;
  onUpdated: (asset: ManifestAsset) => void;
  /** Move-to-InternalAssets + blocklist (the existing disposition), per asset. */
  onRemove: (asset: ManifestAsset) => Promise<void>;
}

type Phase = "edit" | "saving" | "removing" | "error";

interface MetaFields {
  name: string;
  sku: string;
  region: string;
  department: string;
  productCategory: string;
  assetType: string;
  albumPath: string;
}

// Replacement goes up in one PUT (no chunked session), so keep it under the
// origin's body cap. Same kinds as Add: images get a ladder, video/eps/ai/pdf
// are stored as-is.
const MAX_BYTES = 60 * 1024 * 1024;
const ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.mp4,.mov,.webm,.m4v,.eps,.ai,.pdf,image/*,video/*";

function uploadKind(name: string): "image" | "video" | "document" | null {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "image";
  if (["mp4", "mov", "webm", "m4v"].includes(ext)) return "video";
  if (["eps", "ai", "pdf"].includes(ext)) return "document";
  return null;
}

export function EditAssetModal({ asset, taxonomy, albumPaths, onClose, onUpdated, onRemove }: Props) {
  const [fields, setFields] = useState<MetaFields>(() => ({
    name: asset.name ?? "",
    sku: asset.sku ?? "",
    region: asset.region ?? "",
    department: asset.department ?? "",
    productCategory: asset.productCategory ?? "",
    assetType: asset.assetType ?? "",
    albumPath: primaryFolder(asset),
  }));
  // Other folders this asset also appears in (Canto multi-homing, clone-folder,
  // "same video in two folders"). Sent back in full so the server never drops them.
  const [alsoIn, setAlsoIn] = useState<string[]>(() => otherFolders(asset));
  const [extraFolder, setExtraFolder] = useState("");
  const [newFile, setNewFile] = useState<File | null>(null);
  const [newPreview, setNewPreview] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("edit");
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = phase === "saving" || phase === "removing";

  // The current image to show (cached 1200 webp / thumbnail), for reference.
  const currentImg =
    asset.variants
      .filter((v) => v.format === "webp" && v.width > 0 && v.width <= 1200)
      .sort((a, b) => b.width - a.width)[0]?.url ??
    asset.variants.filter((v) => v.width > 0).sort((a, b) => b.width - a.width)[0]?.url;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !busy && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  useEffect(() => () => { if (newPreview) URL.revokeObjectURL(newPreview); }, [newPreview]);

  function chooseFile(f: File | undefined | null) {
    if (!f) return;
    const kind = uploadKind(f.name);
    if (!kind) {
      return setError("Unsupported file type. Allowed: images (JPEG, PNG, WebP, GIF), video (MP4, MOV, WebM), and EPS, AI, or PDF.");
    }
    if (f.size > MAX_BYTES) return setError("File is larger than 60 MB.");
    setError(null);
    setNewFile(f);
    // Only raster images can be shown in an <img>; video/eps/ai/pdf get the chip.
    setNewPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return kind === "image" ? URL.createObjectURL(f) : null;
    });
  }

  function setField(k: keyof MetaFields, v: string) {
    setFields((p) => ({ ...p, [k]: v }));
  }

  async function save() {
    setPhase("saving");
    setError(null);
    try {
      const idToken = await getIdToken();
      const meta: Record<string, unknown> = Object.fromEntries(
        Object.entries(fields).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v),
      );
      // Full folder list = the intent (primary first). Always sent, so removing
      // a folder here works and the server never falls back to guessing.
      meta.albumPaths = [fields.albumPath.trim(), ...alsoIn].filter(Boolean);
      const headers: Record<string, string> = {
        Authorization: `Bearer ${idToken}`,
        "X-Asset-Metadata": utf8ToBase64(JSON.stringify(meta)),
      };
      let body: ArrayBuffer | undefined;
      if (newFile) {
        headers["Content-Type"] = "application/octet-stream";
        headers["X-Filename"] = sanitizeHeader(newFile.name);
        body = await newFile.arrayBuffer();
      }
      const res = await fetch(`/admin/asset/${encodeURIComponent(asset.id)}`, {
        method: "PUT",
        headers,
        body,
      });
      const json = (await res.json().catch(() => ({}))) as { ok?: boolean; asset?: ManifestAsset; error?: string };
      if (!res.ok || !json.ok || !json.asset) throw new Error(json.error || `Update failed (${res.status})`);
      onUpdated(json.asset);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setPhase("error");
    }
  }

  async function remove() {
    setPhase("removing");
    setError(null);
    try {
      await onRemove(asset);
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setPhase("error");
    }
  }

  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="modal add-asset-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-busy={busy}>
        <header>
          <h2>Edit asset</h2>
          <button className="close" aria-label="Close" onClick={onClose} disabled={busy}>×</button>
        </header>
        <div className="modal-body">
          <div
            className={`add-drop ${newPreview ? "add-drop-has" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              chooseFile(e.dataTransfer.files?.[0]);
            }}
          >
            {newPreview ? (
              <img className="add-preview" src={newPreview} alt="new image preview" />
            ) : newFile ? (
              <div className="add-drop-hint">
                <strong>{newFile.name}</strong>
                <span>{uploadKind(newFile.name)} — no inline preview for this type</span>
              </div>
            ) : currentImg ? (
              <div className="edit-current">
                <img className="add-preview" src={currentImg} alt={asset.name} />
                <span className="edit-current-hint">Drop a new file to replace · click to browse</span>
              </div>
            ) : (
              <div className="add-drop-hint">
                <strong>Drop a new file to replace</strong>
                <span>or click to browse — leave as-is to edit metadata only</span>
              </div>
            )}
            <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={(e) => chooseFile(e.target.files?.[0])} />
          </div>
          {newFile && (
            <p className="add-filemeta">
              Replacing with {newFile.name} · {formatBytes(newFile.size)} — the old variants are purged.
            </p>
          )}

          <div className="add-form">
            <label className="add-field add-field-wide">
              <span>Name</span>
              <input value={fields.name} onChange={(e) => setField("name", e.target.value)} />
            </label>
            <label className="add-field">
              <span>SKU</span>
              <input value={fields.sku} onChange={(e) => setField("sku", e.target.value)} placeholder="optional" />
            </label>
            <SelectField label="Region" value={fields.region} options={taxonomy.regions} onChange={(v) => setField("region", v)} />
            <SelectField label="Department" value={fields.department} options={taxonomy.departments} onChange={(v) => setField("department", v)} />
            <SelectField label="Product Category" value={fields.productCategory} options={taxonomy.productCategories} onChange={(v) => setField("productCategory", v)} />
            <SelectField label="Asset Type" value={fields.assetType} options={taxonomy.assetTypes} onChange={(v) => setField("assetType", v)} />
            <label className="add-field add-field-wide">
              <span>Folder</span>
              <input value={fields.albumPath} onChange={(e) => setField("albumPath", e.target.value)} list="edit-album-paths" />
              <datalist id="edit-album-paths">
                {albumPaths.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </label>
            <div className="add-field add-field-wide edit-alsoin">
              <span>Also appears in</span>
              {alsoIn.length > 0 ? (
                <ul className="edit-chips">
                  {alsoIn.map((p) => (
                    <li key={p} className="edit-chip" title={p}>
                      <span>{p}</span>
                      <button
                        type="button"
                        aria-label={`Remove from ${p}`}
                        onClick={() => setAlsoIn((l) => l.filter((x) => x !== p))}
                        disabled={busy}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="edit-alsoin-none">no other folders</span>
              )}
              <div className="edit-alsoin-add">
                <input
                  value={extraFolder}
                  onChange={(e) => setExtraFolder(e.target.value)}
                  list="edit-album-paths"
                  placeholder="add another folder…"
                  disabled={busy}
                />
                <button
                  type="button"
                  className="bulk-btn"
                  disabled={busy || !extraFolder.trim()}
                  onClick={() => {
                    const p = extraFolder.trim().replace(/\/+$/, "");
                    if (p && p !== fields.albumPath.trim() && !alsoIn.includes(p)) setAlsoIn((l) => [...l, p]);
                    setExtraFolder("");
                  }}
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {error && <p className="add-error">{error}</p>}
        </div>
        <footer className="bulk-footer edit-footer">
          {confirmRemove ? (
            <div className="edit-confirm">
              <span>Remove this asset?</span>
              <button className="bulk-btn" onClick={() => setConfirmRemove(false)} disabled={busy}>No</button>
              <button className="bulk-btn edit-remove-go" onClick={() => void remove()} disabled={busy}>
                {phase === "removing" ? "Removing…" : "Yes, remove"}
              </button>
            </div>
          ) : (
            <>
              <button className="bulk-btn edit-remove" onClick={() => setConfirmRemove(true)} disabled={busy}>
                Remove
              </button>
              <span className="edit-footer-spacer" />
              <button className="bulk-btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="bulk-btn bulk-btn-primary" onClick={() => void save()} disabled={busy}>
                {phase === "saving" ? "Saving…" : newFile ? "Replace & save" : "Save changes"}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="add-field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">(none)</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function primaryFolder(asset: ManifestAsset): string {
  return asset.albumPath ?? asset.albumPaths?.[0] ?? "";
}
function otherFolders(asset: ManifestAsset): string[] {
  const primary = primaryFolder(asset);
  return (asset.albumPaths ?? []).filter((p) => p && p !== primary);
}
function sanitizeHeader(s: string): string {
   
  return s.replace(/[^\x20-\x7E]/g, "_");
}
function utf8ToBase64(s: string): string {
  return btoa(unescape(encodeURIComponent(s)));
}
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
